import {
  cn,
  EmojiPicker,
  Menu,
  type MenuAnchor,
  type MenuItem,
  MessageBubble,
  ReactionBar,
  returnFocus,
  Scrim,
  useClampedPlace,
  useEscape,
} from "@rhema/ui";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";

type Props = {
  text: string;
  time: string;
  own: boolean;
  /** The message's actions, from useMessageMenu, so the menu and the shortcuts agree. */
  items: MenuItem[];
  reaction?: string;
  onReact: (emoji: string) => void;
  onClose: () => void;
  /** Desktop: where the pointer was, so the menu opens there. Phones lift the message instead. */
  anchor?: MenuAnchor | null;
  /** The message that opened the menu, which gets focus back when it closes. */
  opener?: HTMLElement | null;
  /** Opens straight on the full emoji picker, as React on the hover bar does. */
  picking?: boolean;
};

/** Closes the menu before running a chosen item, inside submenus too, so a sheet it opens stands alone. */
function closingFirst(items: MenuItem[], close: () => void): MenuItem[] {
  return items.map((item) => {
    if (item.type === "separator") return item;
    if (item.type === "submenu") return { ...item, items: closingFirst(item.items, close) };
    return {
      ...item,
      onSelect: () => {
        close();
        item.onSelect();
      },
    };
  });
}

/**
 * A message's reactions and actions. On desktop they open at the pointer, the reactions above the
 * menu. On phones the message lifts above a scrim with the reactions over it and the menu below.
 * The + opens the full emoji picker. Focus goes back to the message when it closes.
 */
export function MessageActions(props: Props) {
  const { text, time, own, items, reaction, onReact, onClose, anchor, opener } = props;
  const [picking, setPicking] = useState(Boolean(props.picking));
  const desktop = Boolean(anchor);
  const { panel, place } = useClampedPlace(anchor ?? null, desktop, picking);
  const reactions = useRef<HTMLDivElement>(null);
  const menuArea = useRef<HTMLDivElement>(null);
  // Escape closes the picker first when it opened from the menu, and the whole thing otherwise.
  useEscape(!picking || Boolean(props.picking), onClose);

  const openerRef = useRef(opener);
  useEffect(() => {
    const from = openerRef.current ?? (document.activeElement as HTMLElement | null);
    return () => returnFocus(from);
  }, []);

  // Tab moves between the reactions and the menu, and stays inside until the menu closes.
  const firstReaction = () => reactions.current?.querySelector<HTMLElement>("button")?.focus();
  const lastReaction = () => {
    const all = reactions.current?.querySelectorAll<HTMLElement>("button");
    all?.[all.length - 1]?.focus();
  };
  const firstItem = () => menuArea.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
  const onReactionsKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const all = Array.from(reactions.current?.querySelectorAll<HTMLElement>("button") ?? []);
    const i = all.indexOf(document.activeElement as HTMLElement);
    if ((!e.shiftKey && i === all.length - 1) || (e.shiftKey && i === 0)) {
      e.preventDefault();
      firstItem();
    }
  };

  const picker = (
    <EmojiPicker
      label="Add a reaction"
      onPick={(emoji) => onReact(emoji)}
      onClose={() => (props.picking ? onClose() : setPicking(false))}
      className={desktop ? undefined : "w-full"}
    />
  );
  const bar = (
    // biome-ignore lint/a11y/noStaticElementInteractions: Tab from the last reaction moves on to the menu
    <div ref={reactions} onKeyDown={onReactionsKey}>
      <ReactionBar selected={reaction} onReact={onReact} onMore={() => setPicking(true)} />
    </div>
  );
  const menu = (
    <div ref={menuArea}>
      <Menu
        label="Message options"
        items={closingFirst(items, onClose)}
        onClose={onClose}
        onTab={(back) => (back ? lastReaction() : firstReaction())}
        style={desktop ? { maxHeight: "calc(100dvh - 90px)" } : undefined}
      />
    </div>
  );

  if (desktop && anchor) {
    return (
      // A pointer shortcut only, like a scrim. Keyboard users close with Escape.
      // biome-ignore lint/a11y/noStaticElementInteractions: a click outside the menu closes it
      <div
        className="fixed inset-0 z-50"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={panel}
          role="dialog"
          aria-label="Message options"
          className={cn("fixed flex flex-col items-start gap-2", !place && "pointer-events-none opacity-0")}
          style={place ?? { left: anchor.x, top: anchor.y }}
        >
          {picking ? (
            picker
          ) : (
            <>
              <div className="rounded-full shadow-menu">{bar}</div>
              {menu}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Message options" className="absolute inset-0 z-40">
      <Scrim onClick={onClose} />
      <div className="pointer-events-none absolute inset-0 overflow-y-auto">
        <div
          className={cn(
            "mx-4 mt-[110px] mb-24 flex flex-col gap-3 min-[900px]:mt-16 [&>*]:pointer-events-auto",
            own ? "items-end" : "items-start",
          )}
        >
          {!picking && bar}
          <MessageBubble direction={own ? "out" : "in"} time={time} elevated>
            {text}
          </MessageBubble>
          {picking ? picker : menu}
        </div>
      </div>
    </div>
  );
}
